import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  Paper, 
  List, 
  ListItem, 
  ListItemText, 
  Button,
  IconButton,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Tabs,
  Tab
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon,
  Visibility as VisibilityIcon,
  Preview as PreviewIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';
import promptService from '../services/promptService';
import codexTypeService from '../services/codexTypeService';
import ReactMarkdown from 'react-markdown';

const NewPrompt = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;
  
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedCodexType, setSelectedCodexType] = useState('');
  const [selectedFragments, setSelectedFragments] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [fragments, setFragments] = useState([]);
  const [codexTypes, setCodexTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [previewFragment, setPreviewFragment] = useState(null);
  const [previewPrompt, setPreviewPrompt] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [fragmentsData, codexTypesData] = await Promise.all([
          promptService.getAllFragments(),
          codexTypeService.getAll()
        ]);

        setFragments(fragmentsData);
        setCodexTypes(codexTypesData);

        if (isEdit) {
          const promptData = await promptService.getById(id);
          setName(promptData.code || '');
          setDescription(promptData.description || '');
          setSelectedCodexType(promptData.codexType?.id || '');
          setSelectedFragments(promptData.fragments?.map(f => f.id) || []);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        setError("Failed to load data");
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [id, isEdit]);

  const handleSubmit = async () => {
    try {
      if (isEdit) {
        const updateData = {
          description,
          fragmentsIds: selectedFragments
        };
        await promptService.update(id, updateData);
      } else {
        const createData = {
          code: name,
          description,
          codexTypeId: selectedCodexType,
          fragmentIds: selectedFragments
        };
        await promptService.create(createData);
      }
      
      navigate('/prompts');
    } catch (error) {
      console.error('Error saving prompt:', error);
      setError('Failed to save prompt');
    }
  };

  const handleCancel = () => {
    navigate('/prompts');
  };

  const handleAddFragment = (fragmentId) => {
    setSelectedFragments([...selectedFragments, fragmentId]);
  };

  const handleRemoveFragment = (fragmentId) => {
    setSelectedFragments(selectedFragments.filter(id => id !== fragmentId));
  };

  const moveFragmentUp = (index) => {
    if (index === 0) return;
    const newOrder = [...selectedFragments];
    const temp = newOrder[index];
    newOrder[index] = newOrder[index - 1];
    newOrder[index - 1] = temp;
    setSelectedFragments(newOrder);
  };

  const moveFragmentDown = (index) => {
    if (index >= selectedFragments.length - 1) return;
    const newOrder = [...selectedFragments];
    const temp = newOrder[index];
    newOrder[index] = newOrder[index + 1];
    newOrder[index + 1] = temp;
    setSelectedFragments(newOrder);
  };

  const handlePreviewFragment = (fragment) => {
    setPreviewFragment(fragment);
  };

  const handleClosePreview = () => {
    setPreviewFragment(null);
  };

  const handlePreviewPrompt = () => {
    setPreviewPrompt(true);
  };

  const handleClosePromptPreview = () => {
    setPreviewPrompt(false);
  };

  const getFullPromptContent = () => {
    return selectedFragments
      .map(fragId => {
        const fragment = fragments.find(f => f.id === fragId);
        if (!fragment) return '';
        
        // Formatta il contenuto per gestire elenchi puntati e a capo
        let content = fragment.content;
        
        // Gestisce gli elenchi puntati con - o *
        content = content.replace(/^[-*]\s+/gm, '• ');
        
        // Gestisce gli elenchi numerati
        content = content.replace(/^\d+\.\s+/gm, (match) => `${match.trim()} `);
        
        // Gestisce gli a capo multipli
        content = content.replace(/\n{3,}/g, '\n\n');
        
        // Aggiunge spaziatura dopo gli elenchi
        content = content.replace(/([•\d+\.].*)\n/g, '$1\n\n');
        
        return content;
      })
      .join('\n\n');
  };

  const availableFragments = fragments.filter(f => !selectedFragments.includes(f.id));

  const handleCopyPrompt = () => {
    const content = getFullPromptContent();
    navigator.clipboard.writeText(content);
  };

  const handleCopyFragment = () => {
    if (previewFragment) {
      navigator.clipboard.writeText(previewFragment.content);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">
          {isEdit ? 'Modifica Prompt' : 'Nuovo Prompt'}
        </Typography>
        <Stack direction="row" spacing={2}>
          <Button
            startIcon={<PreviewIcon />}
            onClick={handlePreviewPrompt}
            disabled={selectedFragments.length === 0}
          >
            Anteprima
          </Button>
          <Button onClick={handleCancel}>Annulla</Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={!name.trim()}
          >
            Salva
          </Button>
        </Stack>
      </Box>
      
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <TextField
              fullWidth
              label="Codice"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={isEdit}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Descrizione"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              multiline
              rows={2}
              sx={{ mb: 2 }}
            />
            <FormControl fullWidth>
              <InputLabel>Tipo di Codex</InputLabel>
              <Select
                value={selectedCodexType}
                onChange={(e) => setSelectedCodexType(e.target.value)}
                label="Tipo di Codex"
                disabled={isEdit}
              >
                {codexTypes.map(type => (
                  <MenuItem key={type.id} value={type.id}>
                    {type.code}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>Frammenti selezionati</Typography>
            <List>
              {selectedFragments.map((fragId, index) => {
                const fragment = fragments.find(f => f.id === fragId);
                return fragment ? (
                  <ListItem
                    key={fragId}
                    secondaryAction={
                      <Stack direction="row" spacing={1}>
                        <Tooltip title="Anteprima">
                          <IconButton
                            edge="end"
                            onClick={() => handlePreviewFragment(fragment)}
                          >
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>
                        <IconButton
                          edge="end"
                          onClick={() => moveFragmentUp(index)}
                          disabled={index === 0}
                        >
                          <ArrowUpIcon />
                        </IconButton>
                        <IconButton
                          edge="end"
                          onClick={() => moveFragmentDown(index)}
                          disabled={index === selectedFragments.length - 1}
                        >
                          <ArrowDownIcon />
                        </IconButton>
                        <IconButton
                          edge="end"
                          onClick={() => handleRemoveFragment(fragId)}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Stack>
                    }
                  >
                    <ListItemText
                      primary={fragment.code}
                      secondary={
                        <Box sx={{ mt: 0.5 }}>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                            {fragment.description}
                          </Typography>
                          {(fragment.tags || []).map(tag => (
                            <Chip
                              key={tag}
                              label={tag}
                              size="small"
                              sx={{ mr: 0.5 }}
                            />
                          ))}
                        </Box>
                      }
                    />
                  </ListItem>
                ) : null;
              })}
            </List>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>Frammenti disponibili</Typography>
            <TextField
              fullWidth
              placeholder="Cerca frammenti..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="small"
              sx={{ mb: 2 }}
            />
            <List>
              {availableFragments
                .filter(fragment => 
                  searchTerm === '' || 
                  fragment.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
                  fragment.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
                  fragment.description.toLowerCase().includes(searchTerm.toLowerCase())
                )
                .map(fragment => (
                  <ListItem
                    key={fragment.id}
                    secondaryAction={
                      <Stack direction="row" spacing={1}>
                        <Tooltip title="Anteprima">
                          <IconButton
                            edge="end"
                            onClick={() => handlePreviewFragment(fragment)}
                          >
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>
                        <IconButton
                          edge="end"
                          onClick={() => handleAddFragment(fragment.id)}
                        >
                          <AddIcon />
                        </IconButton>
                      </Stack>
                    }
                  >
                    <ListItemText
                      primary={fragment.code}
                      secondary={
                        <Box sx={{ mt: 0.5 }}>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                            {fragment.description}
                          </Typography>
                          {(fragment.tags || []).map(tag => (
                            <Chip
                              key={tag}
                              label={tag}
                              size="small"
                              sx={{ mr: 0.5 }}
                            />
                          ))}
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
            </List>
          </Paper>
        </Grid>
      </Grid>

      <Dialog
        open={!!previewFragment}
        onClose={handleClosePreview}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Anteprima Frammento
          <Typography variant="subtitle2" color="text.secondary">
            {previewFragment?.code}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Box sx={{ 
              '& ul': { 
                pl: 2,
                '& li': {
                  mb: 1
                }
              },
              '& ol': {
                pl: 2,
                '& li': {
                  mb: 1
                }
              },
              '& p': {
                mb: 2
              }
            }}>
              <ReactMarkdown>
                {previewFragment?.content}
              </ReactMarkdown>
            </Box>
            <Box sx={{ mt: 2 }}>
              {(previewFragment?.tags || []).map(tag => (
                <Chip
                  key={tag}
                  label={tag}
                  size="small"
                  sx={{ mr: 0.5 }}
                />
              ))}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button startIcon={<CopyIcon />} onClick={handleCopyFragment}>
            Copia del frammento
          </Button>
          <Button onClick={handleClosePreview}>Chiudi</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={previewPrompt}
        onClose={handleClosePromptPreview}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Anteprima
          <Typography variant="subtitle2" color="text.secondary">
            {name}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Box sx={{ 
              p: 2, 
              bgcolor: 'background.paper', 
              borderRadius: 1,
              '& ul': { 
                pl: 2,
                '& li': {
                  mb: 1
                }
              },
              '& ol': {
                pl: 2,
                '& li': {
                  mb: 1
                }
              },
              '& p': {
                mb: 2
              }
            }}>
              <ReactMarkdown>
                {getFullPromptContent()}
              </ReactMarkdown>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button startIcon={<CopyIcon />} onClick={handleCopyPrompt}>
            Copia del prompt
          </Button>
          <Button onClick={handleClosePromptPreview}>Chiudi</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default NewPrompt; 